import { useGenericShop } from "@/abilities/shop/features/generic/composable/use-generic-shop";
import { useFdiShop } from "@/abilities/shop/features/fdi/composable/use-fdi-shop";
import { useSalesforceShop } from "@/abilities/shop/features/salesforce/composable/use-salesforce-shop";
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { ShopArticleContext } from "@/shared/environment/widgets/widget-context";
import { computed, ref, Ref } from "vue";
import { useSapShop } from "../../sap/composable/use-sap-shop";
import { useShopLight } from "../../shop-light/composable/use-shop-light";
import { useShopStatusStore } from "../store/shop-status.store";

class ShopNotAvailableError extends Error {
    constructor() {
        super("No shop implementation available for the current shop type.");
        this.name = "ShopNotAvailableError";
    }
}

/**
 * Composable to connect to the correct shop implementation
 * Lazy loads shop implementations based on the current shop type
 */
export function useShopConnector(): ShopCore.IShopActions &
    Required<Pick<ShopCore.IShopActions, "sendPriceRequestToShop">> {
    const store = useShopStatusStore();

    // Store the current shop implementation
    const currentShop: Ref<ShopCore.IShopActions | null> = ref(null);

    // Computed property to determine and initialize the correct shop implementation
    const shop = computed(() => {
        if (!currentShop.value) {
            // Lazy initialize the correct shop implementation
            switch (store.shopType) {
                case ShopCore.Type.FDI:
                    currentShop.value = useFdiShop();
                    break;
                case ShopCore.Type.SWAROVSKI:
                    currentShop.value = useSalesforceShop();
                    break;
                case ShopCore.Type.SHOP_LIGHT:
                    currentShop.value = useShopLight();
                    break;
                case ShopCore.Type.BOBST:
                    currentShop.value = useSapShop();
                    break;
                case ShopCore.Type.GENERIC:
                    currentShop.value = useGenericShop();
                    break;
                default:
                    console.warn(`Unsupported shop type: ${store.shopType}`);
                    return null;
            }
        }
        return currentShop.value;
    });

    /**
     * Ensures a shop implementation is available and throws if not
     */
    function ensureShop(): ShopCore.IShopActions {
        if (!shop.value) {
            throw new ShopNotAvailableError();
        }
        return shop.value;
    }

    async function redirectToBasket() {
        try {
            const currentShop = ensureShop();
            const cartId = store.shopType === ShopCore.Type.SWAROVSKI ? await currentShop.getCartId?.() : undefined;
            await currentShop.redirectToBasket(cartId);
        } catch (error) {
            console.warn(error instanceof Error ? error.message : "Failed to redirect to basket");
        }
    }

    async function addPartToShopBasket(
        articleId: string,
        amountSelected: number,
        context?: ShopArticleContext
    ): Promise<void> {
        try {
            const currentShop = ensureShop();
            await currentShop.addPartToShopBasket(articleId, amountSelected, context);
        } catch (error) {
            console.warn(error instanceof Error ? error.message : "Failed to add part to basket");
            throw error;
        }
    }

    async function getShopItemInfoForArticle(
        articleId: string,
        context?: ShopArticleContext
    ): Promise<ShopCore.WidgetItem | null> {
        try {
            const currentShop = ensureShop();
            return await currentShop.getShopItemInfoForArticle(articleId, context);
        } catch (error) {
            console.warn(error instanceof Error ? error.message : "Failed to get shop item info");
            return null;
        }
    }

    async function sendPriceRequestToShop(articleId: string, context?: ShopArticleContext) {
        try {
            const currentShop = ensureShop();
            // A shop without a price request must fail here. Resolving would make
            // the shop widget show "Request sent successfully" for nothing sent.
            if (!currentShop.sendPriceRequestToShop) {
                throw new Error("The shop cannot send price requests.");
            }
            await currentShop.sendPriceRequestToShop(articleId, context);
        } catch (error) {
            console.warn(error instanceof Error ? error.message : "Failed to send price request");
            throw error;
        }
    }

    async function getBasket() {
        try {
            const currentShop = ensureShop();
            return await currentShop.getBasket();
        } catch (error) {
            console.warn(error instanceof Error ? error.message : "Failed to get basket");
            return undefined;
        }
    }

    return {
        addPartToShopBasket,
        getShopItemInfoForArticle,
        sendPriceRequestToShop,
        redirectToBasket,
        getBasket,
    };
}
